import { z } from 'zod';
import { timestampSchema } from './common';

export const ANNOUNCEMENT_TONES = ['info', 'warning'] as const;
export const ANNOUNCEMENT_AUDIENCES = ['everyone', 'parents', 'children'] as const;
export const ANNOUNCEMENT_MAX = 300;

export const announcementSchema = z.object({
  id: z.uuid(),
  message: z.string(),
  tone: z.enum(ANNOUNCEMENT_TONES),
  audience: z.enum(ANNOUNCEMENT_AUDIENCES),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable(),
  state: z.enum(['scheduled', 'live', 'ended']),
  createdBy: z.string().nullable(),
  updatedAt: timestampSchema,
});

export const announcementListSchema = z.object({
  announcements: z.array(announcementSchema),
});

export const announcementInputSchema = z
  .object({
    message: z.string().trim().min(1).max(ANNOUNCEMENT_MAX),
    tone: z.enum(ANNOUNCEMENT_TONES),
    audience: z.enum(ANNOUNCEMENT_AUDIENCES),
    startsAt: z.iso.datetime({ offset: true }).nullable().default(null),
    endsAt: z.iso.datetime({ offset: true }).nullable().default(null),
  })
  .refine(
    (input) =>
      input.endsAt === null ||
      new Date(input.endsAt).getTime() > new Date(input.startsAt ?? Date.now()).getTime(),
    { message: 'The end must be after the start', path: ['endsAt'] },
  );

export const currentAnnouncementQuerySchema = z.object({
  for: z.enum(['parents', 'children']),
});

export const currentAnnouncementSchema = z.object({
  announcement: z
    .object({
      id: z.uuid(),
      message: z.string(),
      tone: z.enum(ANNOUNCEMENT_TONES),
    })
    .nullable(),
});

export type Announcement = z.infer<typeof announcementSchema>;
export type AnnouncementList = z.infer<typeof announcementListSchema>;
export type AnnouncementInput = z.input<typeof announcementInputSchema>;
export type AnnouncementFields = z.output<typeof announcementInputSchema>;
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];
export type CurrentAnnouncement = z.infer<typeof currentAnnouncementSchema>;
